import { useEffect, useState, type FormEvent } from "react";
import { auth, companies, push as pushApi } from "../api/client";
import type { ApiKey, CompanyRole, PostalAddress } from "../api/types";
import { companyCanWrite, isOwner, useMe, useSession } from "../auth/session";
import { useNavigate } from "react-router";
import { Page } from "../components/Shell";
import { Empty, ErrorBox, Field, Pill, Spinner, errorMessage, useToast } from "../components/ui";
import { useAsync } from "../hooks/useAsync";
import { fmt, t } from "../i18n";
import { dateShort, dateTimeShort, formatCNPJ } from "../lib/format";
import { brl } from "../lib/money";
import { isValidEmail } from "../lib/validators";
import { currentSubscription, pushSupported, subscribePush, unsubscribePush } from "../lib/push";

export function Configuracoes() {
  const me = useMe();
  const hasCompany = Boolean(me.company);

  return (
    <Page title={t.settings.title}>
      <div className="grid2">
        <div className="card pad stack s12">
          <h2>{t.settings.account}</h2>
          <div className="kv"><span className="k">{t.auth.name}</span><span className="v">{me.user.name}</span></div>
          <div className="kv"><span className="k">{t.auth.email}</span><span className="v">{me.user.email}</span></div>
          {me.role && <div className="kv"><span className="k">{t.settings.colRole}</span><span className="v">{t.roles[me.role]}</span></div>}
          {me.user.staffRole && <div className="kv"><span className="k">{t.nav.toca}</span><span className="v">{me.user.staffRole}</span></div>}
        </div>
        {me.company && (
          <div className="card pad stack s12">
            <h2>{t.settings.limits}</h2>
            <span className="muted small">{t.settings.limitsLead}</span>
            <div className="kv"><span className="k">{t.settings.company}</span><span className="v">{me.company.legalName} · {formatCNPJ(me.company.cnpj)}</span></div>
            <div className="kv"><span className="k">{t.settings.perTx}</span><span className="v mono">{brl(me.company.limits.perTxCents)}</span></div>
            <div className="kv"><span className="k">{t.settings.monthly}</span><span className="v mono">{brl(me.company.limits.monthlyCents)}</span></div>
            <div className="kv"><span className="k">{t.settings.usedMonth}</span><span className="v mono">{brl(me.company.monthUsedCents)}</span></div>
          </div>
        )}
      </div>
      <div className="grid2">
        <SecurityCard />
        {hasCompany && <CompanyCard />}
      </div>
      {hasCompany && <PushCard />}
      {hasCompany && <UsersCard />}
      {hasCompany && me.company?.status === "approved" && <ApiKeysCard />}
    </Page>
  );
}

function ApiKeysCard() {
  const me = useMe();
  const toast = useToast();
  const owner = isOwner(me);
  const { data, error, loading, reload } = useAsync(() => companies.apiKeys(), []);
  const [name, setName] = useState("");
  const [pending, setPending] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [fresh, setFresh] = useState<{ name: string; secret: string } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    if (name.trim().length < 2) return setErr(t.settings.apiKeyNameHint);
    setPending(true);
    try {
      const r = await companies.createApiKey(name.trim());
      setFresh({ name: r.apiKey.name, secret: r.secret });
      setName("");
      void reload();
    } catch (x) {
      setErr(errorMessage(x));
    } finally {
      setPending(false);
    }
  }

  async function copySecret() {
    if (!fresh) return;
    try {
      await navigator.clipboard.writeText(fresh.secret);
      toast(t.settings.apiKeyCopied);
    } catch {
      toast(t.common.copyFailed);
    }
  }

  async function revoke(k: ApiKey) {
    if (!window.confirm(fmt(t.settings.apiKeyRevokeConfirm, { name: k.name }))) return;
    setBusy(k.id);
    try {
      await companies.revokeApiKey(k.id);
      toast(t.settings.apiKeyRevoked);
      void reload();
    } catch (x) {
      toast(errorMessage(x));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="two n400">
      <div className="card">
        <div className="card-head">
          <div className="stack" style={{ gap: 2 }}>
            <h2>{t.settings.apiKeys}</h2>
            <span className="muted small">{t.settings.apiKeysLead}</span>
          </div>
        </div>
        {loading && !data ? (
          <Spinner />
        ) : error ? (
          <div style={{ padding: 16 }}><ErrorBox error={error} onRetry={reload} /></div>
        ) : !data || data.length === 0 ? (
          <Empty body={t.settings.apiKeyNone} />
        ) : (
          <div className="tscroll" tabIndex={0}>
            <table>
              <thead>
                <tr>
                  <th>{t.settings.apiKeyColName}</th>
                  <th>{t.settings.apiKeyColPrefix}</th>
                  <th>{t.settings.apiKeyColCreated}</th>
                  <th>{t.settings.apiKeyColLastUsed}</th>
                  {owner && <th />}
                </tr>
              </thead>
              <tbody>
                {data.map((k) => (
                  <tr key={k.id} className={k.revokedAt ? "revoked" : undefined}>
                    <td style={{ fontWeight: 600 }}>
                      {k.name} {k.revokedAt && <Pill tone="mute">{t.settings.apiKeyRevokedTag}</Pill>}
                    </td>
                    <td className="mono">{k.prefix}…</td>
                    <td className="ink2">{dateShort(k.createdAt)}</td>
                    <td className="ink2">{k.lastUsedAt ? dateTimeShort(k.lastUsedAt) : t.settings.apiKeyNeverUsed}</td>
                    {owner && (
                      <td style={{ textAlign: "right" }}>
                        {!k.revokedAt && (
                          <button type="button" className="btn ghost crit" onClick={() => revoke(k)} disabled={busy === k.id}>
                            {t.settings.apiKeyRevoke}
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="muted small" style={{ padding: "12px 20px 18px" }}>{t.settings.apiKeyDocs}</div>
      </div>
      <div className="card">
        {fresh ? (
          <div className="stack" style={{ padding: 22, gap: 14 }}>
            <h2>{t.settings.apiKeySecretTitle}</h2>
            <span className="ink2" style={{ fontSize: 13.5 }}>{t.settings.apiKeySecretLead}</span>
            <code className="mono" style={{ display: "block", padding: 12, borderRadius: 10, background: "var(--surface-2)", wordBreak: "break-all", fontSize: 13 }} aria-label={fresh.name}>
              {fresh.secret}
            </code>
            <div className="row" style={{ gap: 10 }}>
              <button type="button" className="btn" onClick={copySecret}>{t.settings.apiKeyCopy}</button>
              <button type="button" className="btn ghost" onClick={() => setFresh(null)}>{t.settings.apiKeyDone}</button>
            </div>
          </div>
        ) : owner ? (
          <form onSubmit={submit} style={{ padding: 22, display: "flex", flexDirection: "column", gap: 14 }} noValidate>
            <h2>{t.settings.apiKeyCreate}</h2>
            <Field label={t.settings.apiKeyName} hint={t.settings.apiKeyNameHint}>
              {(id, d) => <div className="inp"><input id={id} value={name} onChange={(e) => setName(e.target.value)} aria-describedby={d} maxLength={60} /></div>}
            </Field>
            {err && <div className="form-error" role="alert">{err}</div>}
            <button type="submit" className="btn full" disabled={pending}>{pending ? t.settings.apiKeyCreating : t.settings.apiKeyCreate}</button>
          </form>
        ) : (
          <div className="pad" style={{ padding: 22 }}><span className="muted">{t.settings.onlyOwnerKeys}</span></div>
        )}
      </div>
    </div>
  );
}

function UsersCard() {
  const me = useMe();
  const toast = useToast();
  const { data, error, loading, reload } = useAsync(() => companies.users(), []);
  const owner = isOwner(me);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<CompanyRole>("finance");
  const [password, setPassword] = useState("");
  const [pending, setPending] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    if (!name.trim() || !email || !password) return setErr(t.auth.fillAll);
    if (!isValidEmail(email)) return setErr(t.auth.invalidEmail);
    if (password.length < 10) return setErr(t.auth.weakPassword);
    setPending(true);
    try {
      await companies.createUser({ name: name.trim(), email: email.trim(), role, password });
      toast(t.settings.created);
      setName("");
      setEmail("");
      setPassword("");
      void reload();
    } catch (x) {
      setErr(errorMessage(x));
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="two n400">
      <div className="card">
        <div className="card-head">
          <div className="stack" style={{ gap: 2 }}>
            <h2>{t.settings.users}</h2>
            <span className="muted small">{t.settings.usersLead}</span>
          </div>
        </div>
        {loading && !data ? (
          <Spinner />
        ) : error ? (
          <div style={{ padding: 16 }}><ErrorBox error={error} onRetry={reload} /></div>
        ) : !data || data.length === 0 ? (
          <Empty />
        ) : (
          <div className="tscroll" tabIndex={0}>
            <table>
              <thead>
                <tr>
                  <th>{t.settings.colName}</th>
                  <th>{t.settings.colEmail}</th>
                  <th>{t.settings.colRole}</th>
                </tr>
              </thead>
              <tbody>
                {data.map((u) => (
                  <tr key={u.userId ?? u.id ?? u.email}>
                    <td style={{ fontWeight: 600 }}>{u.name}</td>
                    <td className="ink2">{u.email}</td>
                    <td><Pill tone={u.role === "owner" ? "go" : u.role === "finance" ? "ok" : "mute"}>{t.roles[u.role] ?? u.role}</Pill></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <div className="card">
        {owner ? (
          <form onSubmit={submit} style={{ padding: 22, display: "flex", flexDirection: "column", gap: 14 }} noValidate>
            <h2>{t.settings.newUser}</h2>
            <Field label={t.settings.userName}>{(id) => <div className="inp"><input id={id} value={name} onChange={(e) => setName(e.target.value)} /></div>}</Field>
            <Field label={t.settings.userEmail}>{(id) => <div className="inp"><input id={id} type="email" value={email} onChange={(e) => setEmail(e.target.value)} /></div>}</Field>
            <Field label={t.settings.userRole}>
              {(id) => (
                <div className="inp">
                  <select id={id} value={role} onChange={(e) => setRole(e.target.value as CompanyRole)}>
                    <option value="owner">{t.roles.owner}</option>
                    <option value="finance">{t.roles.finance}</option>
                    <option value="viewer">{t.roles.viewer} · {t.auth.accessAccountant}</option>
                  </select>
                </div>
              )}
            </Field>
            <Field label={t.settings.userPassword} hint={t.settings.userPasswordHint}>
              {(id, d) => <div className="inp"><input id={id} type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} aria-describedby={d} /></div>}
            </Field>
            {err && <div className="form-error" role="alert">{err}</div>}
            <button type="submit" className="btn full" disabled={pending}>{pending ? t.settings.creating : t.settings.create}</button>
          </form>
        ) : (
          <div className="pad" style={{ padding: 22 }}><span className="muted">{t.settings.onlyOwner}</span></div>
        )}
      </div>
    </div>
  );
}

function PushCard() {
  const toast = useToast();
  const { data, error, loading, reload } = useAsync(() => pushApi.status(), []);
  const [active, setActive] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const supported = pushSupported();

  useEffect(() => {
    if (!supported) { setActive(false); return; }
    void currentSubscription().then((s) => setActive(Boolean(s)));
  }, [supported]);

  async function toggle() {
    if (!data?.publicKey) return;
    setBusy(true);
    try {
      if (active) {
        await unsubscribePush();
        setActive(false);
        toast(t.settings.pushInactive);
      } else {
        const r = await subscribePush(data.publicKey);
        if (r === "subscribed") { setActive(true); toast(t.settings.pushActive); }
        else toast(r === "denied" ? t.settings.pushDenied : t.settings.pushUnsupported);
      }
      void reload();
    } catch (x) {
      toast(errorMessage(x));
    } finally {
      setBusy(false);
    }
  }

  const status = !supported ? t.settings.pushUnsupported : data && !data.enabled ? t.settings.pushDisabled : active ? t.settings.pushActive : t.settings.pushInactive;
  return (
    <div className="card pad stack s12">
      <h2>{t.settings.push}</h2>
      <span className="muted small">{t.settings.pushLead}</span>
      {loading && !data ? <Spinner /> : error ? <ErrorBox error={error} onRetry={reload} /> : (
        <div className="row between" style={{ gap: 12, flexWrap: "wrap" }}>
          <div className="stack" style={{ gap: 2 }}>
            <span className="ink2">{status}</span>
            {data?.enabled && <span className="muted small">{fmt(t.settings.pushDevices, { n: data.subscriptions })}</span>}
          </div>
          {supported && data?.enabled && (
            <button type="button" className={`btn ${active ? "ghost" : ""}`} onClick={toggle} disabled={busy || active === null} aria-pressed={active ?? false}>
              {busy ? t.settings.pushWorking : active ? t.settings.pushOff : t.settings.pushOn}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function SecurityCard() {
  const toast = useToast();
  const navigate = useNavigate();
  const { logout } = useSession();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [pending, setPending] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [totpAsk, setTotpAsk] = useState(false);
  const [totpPassword, setTotpPassword] = useState("");
  const [totpBusy, setTotpBusy] = useState(false);
  const [totpErr, setTotpErr] = useState<string | null>(null);

  async function changePassword(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    if (!current || !next || !confirm) return setErr(t.auth.fillAll);
    if (next.length < 10) return setErr(t.auth.weakPassword);
    if (next !== confirm) return setErr(t.auth.passwordsDiffer);
    setPending(true);
    try {
      await auth.changePassword(current, next);
      toast(t.settings.passwordChanged);
      setCurrent(""); setNext(""); setConfirm("");
    } catch (x) {
      setErr(errorMessage(x));
    } finally {
      setPending(false);
    }
  }

  async function reconfigureTotp(e: FormEvent) {
    e.preventDefault();
    setTotpErr(null);
    if (!totpPassword) return setTotpErr(t.auth.fillAll);
    setTotpBusy(true);
    try {
      await auth.reconfigureTotp(totpPassword);
      toast(t.settings.totpReconfigured);
      await logout().catch(() => {});
      navigate("/entrar", { replace: true });
    } catch (x) {
      setTotpErr(errorMessage(x));
    } finally {
      setTotpBusy(false);
      setTotpPassword("");
    }
  }

  return (
    <div className="card pad stack s12">
      <h2>{t.settings.security}</h2>
      <span className="muted small">{t.settings.securityLead}</span>
      <form onSubmit={changePassword} className="stack" noValidate>
        <Field label={t.settings.currentPassword}>{(id) => <div className="inp"><input id={id} type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} /></div>}</Field>
        <Field label={t.auth.newPassword} hint={t.auth.passwordHint}>{(id, d) => <div className="inp"><input id={id} type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} aria-describedby={d} /></div>}</Field>
        <Field label={t.auth.confirmPassword}>{(id) => <div className="inp"><input id={id} type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} /></div>}</Field>
        {err && <div className="form-error" role="alert">{err}</div>}
        <button type="submit" className="btn" disabled={pending}>{pending ? t.common.loading : t.settings.changePassword}</button>
      </form>
      <hr style={{ border: 0, borderTop: "1px solid var(--line)", margin: "4px 0" }} />
      <div className="stack s8">
        <b>{t.settings.totpReconfigure}</b>
        <span className="ink2" style={{ fontSize: 13.5 }}>{t.settings.totpReconfigureLead}</span>
        {totpAsk ? (
          <form onSubmit={reconfigureTotp} className="stack" noValidate>
            <Field label={t.auth.password}>{(id) => <div className="inp"><input id={id} type="password" autoComplete="current-password" value={totpPassword} onChange={(e) => setTotpPassword(e.target.value)} /></div>}</Field>
            {totpErr && <div className="form-error" role="alert">{totpErr}</div>}
            <div className="row" style={{ gap: 10 }}>
              <button type="submit" className="btn crit" disabled={totpBusy}>{totpBusy ? t.common.loading : t.settings.totpReconfigureConfirm}</button>
              <button type="button" className="btn ghost" onClick={() => setTotpAsk(false)}>{t.common.cancel}</button>
            </div>
          </form>
        ) : (
          <div><button type="button" className="btn ghost" onClick={() => setTotpAsk(true)}>{t.settings.totpReconfigure}</button></div>
        )}
      </div>
    </div>
  );
}

function CompanyCard() {
  const me = useMe();
  const toast = useToast();
  const canWrite = companyCanWrite(me);
  const { data, error, loading, reload } = useAsync(() => companies.current(), []);
  const [tradeName, setTradeName] = useState<string | null>(null);
  const [address, setAddress] = useState<PostalAddress | null>(null);
  const [pending, setPending] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const tn = tradeName ?? data?.tradeName ?? "";
  const ad: PostalAddress = address ?? data?.address ?? { line1: "", city: "", state: "", postalCode: "" };
  const setAd = (p: Partial<PostalAddress>) => setAddress({ ...ad, ...p });

  async function save(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    if (!ad.line1.trim() || !ad.city.trim() || !ad.state.trim() || !ad.postalCode.trim()) return setErr(t.auth.fillAll);
    setPending(true);
    try {
      await companies.update({ tradeName: tn.trim() || undefined, address: { line1: ad.line1.trim(), city: ad.city.trim(), state: ad.state.trim().toUpperCase(), postalCode: ad.postalCode.trim() } });
      toast(t.settings.savedCompany);
      setTradeName(null); setAddress(null);
      void reload();
    } catch (x) {
      setErr(errorMessage(x));
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="card pad stack s12">
      <h2>{t.settings.companyData}</h2>
      <span className="muted small">{t.settings.companyDataLead}</span>
      {loading && !data ? <Spinner /> : error ? <ErrorBox error={error} onRetry={reload} /> : (
        <form onSubmit={save} className="stack" noValidate>
          <div className="kv"><span className="k">{t.settings.company}</span><span className="v">{data?.legalName} · {formatCNPJ(data?.cnpj ?? "")}</span></div>
          <Field label={t.settings.tradeName}>{(id) => <div className="inp"><input id={id} value={tn} onChange={(e) => setTradeName(e.target.value)} disabled={!canWrite} maxLength={120} /></div>}</Field>
          <Field label={t.onboarding.addressLine1}>{(id) => <div className="inp"><input id={id} value={ad.line1} onChange={(e) => setAd({ line1: e.target.value })} disabled={!canWrite} maxLength={200} /></div>}</Field>
          <div className="grid2">
            <Field label={t.onboarding.addressCity}>{(id) => <div className="inp"><input id={id} value={ad.city} onChange={(e) => setAd({ city: e.target.value })} disabled={!canWrite} maxLength={100} /></div>}</Field>
            <Field label={t.onboarding.addressState}>{(id) => <div className="inp"><input id={id} className="mono" value={ad.state} onChange={(e) => setAd({ state: e.target.value.toUpperCase() })} disabled={!canWrite} maxLength={2} /></div>}</Field>
            <Field label={t.onboarding.addressPostalCode}>{(id) => <div className="inp"><input id={id} className="mono" inputMode="numeric" value={ad.postalCode} onChange={(e) => setAd({ postalCode: e.target.value })} disabled={!canWrite} maxLength={9} /></div>}</Field>
          </div>
          {err && <div className="form-error" role="alert">{err}</div>}
          {canWrite && <button type="submit" className="btn" disabled={pending}>{pending ? t.common.loading : t.settings.saveCompany}</button>}
        </form>
      )}
    </div>
  );
}
